import { createContext, useContext, useEffect, useState } from "react";

const ConfigContext = createContext();
const LOCALHOST_FALLBACK_DOMAIN =
  process.env.LOCALHOST_FALLBACK_DOMAIN ||
  process.env.NEXT_PUBLIC_LOCALHOST_FALLBACK_DOMAIN ||
  "store.tukuya.com";

// Fungsi untuk check apakah domain adalah localhost
const isLocalhost = (domain) => {
  if (!domain) return false;
  return (
    domain === "localhost" ||
    domain === "127.0.0.1" ||
    domain.startsWith("localhost:") ||
    domain.startsWith("127.0.0.1:")
  );
};

// Fungsi untuk mendapatkan domain yang akan digunakan untuk API
// Jika localhost, default ke store.tukuya.com
// Jika bukan localhost, gunakan domain yang sedang diakses
const getApiDomain = (domain) => {
  if (!domain) return LOCALHOST_FALLBACK_DOMAIN;

  // Jika localhost, default ke store.tukuya.com
  if (isLocalhost(domain)) {
    return LOCALHOST_FALLBACK_DOMAIN;
  }

  // Jika bukan localhost, gunakan domain yang sedang diakses
  return domain;
};

// Fungsi untuk load config dari API
// Menggunakan Next.js API route sebagai proxy untuk menghindari masalah CORS
const loadConfigFromAPI = async (domain) => {
  try {
    // Tentukan domain yang akan digunakan untuk API
    const apiDomain = getApiDomain(domain);

    // Gunakan Next.js API route sebagai proxy untuk menghindari CORS
    const proxyUrl = `/api/web-configs?domain=${encodeURIComponent(apiDomain)}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000); // 15 detik timeout (lebih lama karena melalui proxy)

    let response;
    try {
      response = await fetch(proxyUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        cache: "no-cache",
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
    } catch (fetchError) {
      clearTimeout(timeoutId);

      // Handle different types of fetch errors
      if (fetchError.name === "AbortError") {
        throw new Error("Request timeout: API tidak merespons dalam 15 detik");
      } else if (fetchError.message.includes("Failed to fetch")) {
        throw new Error(
          `Gagal menghubungi API melalui proxy. Pastikan:\n` +
            `1. Koneksi internet tersedia\n` +
            `2. Next.js API route dapat diakses\n` +
            `3. API endpoint dapat diakses dari server\n` +
            `Error: ${fetchError.message}`,
        );
      } else {
        throw new Error(`Network error: ${fetchError.message}`);
      }
    }

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: response.statusText };
      }

      throw new Error(
        `API Error ${response.status}: ${
          errorData.message || response.statusText
        }`,
      );
    }

    let responseData;
    try {
      responseData = await response.json();
    } catch (jsonError) {
      throw new Error("Invalid JSON response from API");
    }

    // Validasi response memiliki struktur yang benar
    if (!responseData || typeof responseData !== "object") {
      throw new Error("Invalid API response format: Response is not an object");
    }

    if (responseData.code !== 200) {
      throw new Error(
        `API returned error code: ${responseData.code}. Message: ${
          responseData.message || "Unknown error"
        }`,
      );
    }

    if (!responseData.data) {
      throw new Error("Invalid API response format: Missing data property");
    }

    // Return data lengkap dari API
    return responseData.data;
  } catch (error) {
    console.error(`Error loading config for domain ${domain}:`, error);
    // Re-throw dengan pesan yang lebih informatif
    if (error.message) {
      throw error;
    } else {
      throw new Error(`Unexpected error: ${error.toString()}`);
    }
  }
};

const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [isConfigReady, setIsConfigReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentDomain, setCurrentDomain] = useState(null);

  // State untuk menyimpan data lengkap dari API
  const [configData, setConfigData] = useState(null); // Menyimpan data lengkap (id, team_id, store_id, domain, config, is_active, dll)
  const [apiDomain, setApiDomain] = useState(null); // Domain yang digunakan untuk hit API

  // Fungsi untuk mendapatkan domain dari browser
  const getDomain = () => {
    if (typeof window === "undefined") {
      return null;
    }
    return window.location.hostname;
  };

  // Fungsi untuk mendapatkan domain yang digunakan untuk API
  const getApiDomainForContext = (domain) => {
    return getApiDomain(domain);
  };

  // Fungsi untuk load config berdasarkan domain
  const loadConfig = async (domain) => {
    if (!domain) {
      setError("Domain tidak ditemukan");
      setLoading(false);
      setIsConfigReady(true);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Tentukan domain yang akan digunakan untuk API
      const domainForAPI = getApiDomain(domain);
      setApiDomain(domainForAPI);

      // Load config dari API
      const apiData = await loadConfigFromAPI(domain);

      // Handle struktur data: bisa langsung atau nested dalam config
      // Format baru dari /api/web-configs selalu memiliki config nested
      // Tapi untuk backward compatibility, cek apakah config ada
      const configObject = {
        ...(apiData.config || apiData),
        affiliate: apiData.affiliate || null,
      };
      const uiComponents =
        apiData.ui_components ||
        apiData.config?.ui_components ||
        configObject.ui_components ||
        null;

      // Simpan data lengkap dari API dengan apiDomain
      const configDataWithApiDomain = {
        id: apiData.id,
        team_id: apiData.team_id,
        store_id: apiData.store_id, // Tambahkan store_id
        domain: apiData.domain,
        apiDomain: domainForAPI, // Simpan apiDomain yang digunakan
        config: configObject,
        ui_components: uiComponents, // Include ui_components
        is_active: apiData.is_active,
        created_at: apiData.created_at,
        updated_at: apiData.updated_at,
      };

      setConfigData(configDataWithApiDomain);

      // Set config untuk backward compatibility (extract dari data.config atau data langsung)
      setConfig(configObject);
      setCurrentDomain(domain);

      // Cache is optional: a storage failure must never replace fresh API data.
      try {
        localStorage.setItem(`config_${domain}`, JSON.stringify(configObject));
        localStorage.setItem(
          `configData_${domain}`,
          JSON.stringify(configDataWithApiDomain),
        );
        localStorage.setItem("lastConfigDomain", domain);
      } catch {
        // Storage can be unavailable or full; keep the current API configuration.
      }

      setError(null);
    } catch (err) {
      console.error("Error loading config:", err);

      const apiDomainForError = getApiDomain(domain);
      const apiUrlForError = `/api/web-configs?domain=${encodeURIComponent(
        apiDomainForError,
      )}`;

      // Pastikan error message aman untuk ditampilkan
      const errorMessage = err?.message || err?.toString() || "Unknown error";
      const safeErrorMessage =
        typeof errorMessage === "string"
          ? errorMessage.substring(0, 500) // Batasi panjang error message
          : "Unknown error";

      // Coba load dari cache jika ada
      let cachedConfigData = null;
      let cachedConfig = null;
      try {
        cachedConfigData = localStorage.getItem(`configData_${domain}`);
        cachedConfig = localStorage.getItem(`config_${domain}`);
      } catch {
        // Report the API error below when browser storage is unavailable too.
      }

      if (cachedConfigData && cachedConfig) {
        try {
          const parsedConfigData = JSON.parse(cachedConfigData);
          const parsedConfig = JSON.parse(cachedConfig);

          // Pastikan apiDomain ada di configData dari cache
          const cachedConfigDataWithApiDomain = {
            ...parsedConfigData,
            apiDomain: parsedConfigData.apiDomain || apiDomainForError,
          };

          setConfigData(cachedConfigDataWithApiDomain);
          setConfig(parsedConfig);
          setCurrentDomain(domain);
          setApiDomain(apiDomainForError);
          setError(
            `Menggunakan config dari cache (API tidak dapat diakses)\n` +
              `URL yang dicoba: ${apiUrlForError}\n` +
              `Error: ${safeErrorMessage}`,
          );
        } catch (parseError) {
          console.error("Error parsing cached config:", parseError);
          setError(
            `Gagal memuat config dari API dan cache\n` +
              `URL yang dicoba: ${apiUrlForError}\n` +
              `Error: ${safeErrorMessage}`,
          );
        }
      } else {
        setError(
          `Gagal memuat config dari API\n` +
            `URL yang dicoba: ${apiUrlForError}\n` +
            `Error: ${safeErrorMessage}`,
        );
      }
    } finally {
      setLoading(false);
      setIsConfigReady(true);
    }
  };

  // Load config saat mount
  useEffect(() => {
    const domain = getDomain();
    if (domain) {
      loadConfig(domain);
    } else {
      setLoading(false);
      setIsConfigReady(true);
    }
  }, []);

  // Restoring a page from the back/forward cache does not remount React.
  useEffect(() => {
    const handlePageShow = (event) => {
      if (!event.persisted) return;
      const domain = getDomain();
      if (domain) void loadConfig(domain);
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  // Reload config jika domain berubah (skip saat initial mount)
  useEffect(() => {
    if (!currentDomain) return;
    const domain = getDomain();
    if (domain && domain !== currentDomain) {
      loadConfig(domain);
    }
  }, [currentDomain]);

  // Fungsi untuk reload config manual
  const reloadConfig = async () => {
    const domain = getDomain();
    if (domain) {
      // Clear cache
      try {
        localStorage.removeItem(`config_${domain}`);
        localStorage.removeItem(`configData_${domain}`);
      } catch {
        // Reload from the API even when cached entries cannot be removed.
      }
      await loadConfig(domain);
    }
  };

  return (
    <ConfigContext.Provider
      value={{
        config, // Config object untuk backward compatibility
        configData, // Data lengkap dari API (id, team_id, store_id, domain, config, is_active, dll)
        isConfigReady,
        loading,
        error,
        currentDomain,
        apiDomain, // Domain yang digunakan untuk hit API
        reloadConfig,
        getDomain,
        getApiDomain: getApiDomainForContext,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
};

const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error("useConfig must be used within ConfigProvider");
  }
  return context;
};

export { ConfigProvider, useConfig };
